import React from 'react';
import {
  Button,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import AddCommentIcon from '@mui/icons-material/AddComment';

/**
 * ChatControls Component
 * Renders the chat control bar with persona selector, new chat button, and action buttons
 * 
 * @param {Object} props - Component props
 * @param {string} props.chatPhase - Current chat phase
 * @param {boolean} props.initialLoading - Whether initial loading is in progress
 * @param {Array} props.personas - Available personas
 * @param {Object} props.user - User object with UI options
 * @param {string} props.selectedPersonaForNewChat - Currently selected persona for new chat
 * @param {Function} props.setSelectedPersonaForNewChat - Function to update selected persona
 * @param {Function} props.onStartNewChat - Handler for new chat button
 * @param {boolean} props.isNewChat - Whether this is a new chat
 * @param {Array} props.messages - Chat messages
 * @param {string} props.currentConversationId - Current conversation ID
 * @param {string} props.currentPersona - Current conversation persona
 * @param {boolean} props.sessionsEnabled - Whether sessions are enabled
 * @param {Object} props.clientTherapySettings - Client therapy settings
 * @param {Function} props.handleRequestReview - Handler for request review button
 * @param {Object} props.reviewRequestStatus - Review request status object
 * @param {boolean} props.isMobile - Whether on mobile device
 * @returns {JSX.Element|null} ChatControls component or null if not applicable
 */
const ChatControls = ({
  chatPhase,
  initialLoading,
  personas = [],
  user,
  selectedPersonaForNewChat,
  setSelectedPersonaForNewChat,
  onStartNewChat,
  isNewChat,
  messages = [],
  currentConversationId,
  currentPersona,
  sessionsEnabled,
  clientTherapySettings,
  handleRequestReview,
  reviewRequestStatus = {},
  isMobile = false,
  onOpenSupportTicket,
  token,
  onVoiceChanged
}) => {
  const showPersonaSelector = user?.clientUiOptions?.showPersonaSelector || false;
  const showNewChatButton = user?.clientUiOptions?.showNewChatButton || false;

  // v2: the chat identity + Talk toggle + Choose Voice / Get Help / Request
  // Review now live in the merged PersonaHeader. This bar only remains for the
  // (admin-gated, usually off) persona selector / new-chat controls — so it
  // renders nothing for most clients.
  if (
    chatPhase !== 'main' || initialLoading || personas.length === 0 || isMobile ||
    (!showPersonaSelector && !showNewChatButton)
  ) {
    return null;
  }

  return (
    <Paper
      elevation={1}
      sx={{
        p: 1.5,
        borderBottom: '1px solid',
        borderColor: 'divider',
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        flexWrap: 'wrap'
      }}
    >
      {/* Persona Selector */}
      {showPersonaSelector && (
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel id="persona-select-label">AI Persona</InputLabel>
          <Select
            labelId="persona-select-label"
            id="persona-select"
            value={selectedPersonaForNewChat}
            label="AI Persona"
            onChange={(e) => setSelectedPersonaForNewChat(e.target.value)}
          >
            {personas.map(p => (
              <MenuItem key={p.key} value={p.key}>
                {p.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      {/* New Chat Button */}
      {showNewChatButton && (
        <Button
          variant="outlined"
          size="small"
          onClick={onStartNewChat}
          startIcon={<AddCommentIcon />}
        >
          Start New Chat
        </Button>
      )}
    </Paper>
  );
};

export default ChatControls;